import { useMemo, useState, type KeyboardEvent } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Trash2 } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import {
  useCreateFileComment,
  useCreateRequestComment,
  useDeleteComment,
  useFileComments,
  useRequestComments,
} from '@/api/comments';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { apiError } from '@/lib/api';
import type { Comment, Role } from '@/types/api';

interface Props {
  target: { type: 'file' | 'file_request'; id: string };
}

const STAFF_PLUS_ROLES: Role[] = ['staff', 'manager', 'sysadmin'];

function initialsFor(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

export function CommentsPanel({ target }: Props) {
  const { user, hasRole } = useAuth();
  const toast = useToast();

  const isFile = target.type === 'file';
  const fileQuery = useFileComments(isFile ? target.id : undefined);
  const requestQuery = useRequestComments(!isFile ? target.id : undefined);
  const active = isFile ? fileQuery : requestQuery;

  const createFile = useCreateFileComment();
  const createRequest = useCreateRequestComment();
  const deleteComment = useDeleteComment();

  const [body, setBody] = useState('');
  const isStaffPlus = hasRole(STAFF_PLUS_ROLES);

  const comments: Comment[] = useMemo(() => active.data?.items ?? [], [active.data]);
  const isPosting = isFile ? createFile.isPending : createRequest.isPending;

  const submit = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      if (isFile) {
        await createFile.mutateAsync({ fileId: target.id, body: trimmed });
      } else {
        await createRequest.mutateAsync({ requestId: target.id, body: trimmed });
      }
      setBody('');
    } catch (e) {
      toast.error('Could not post comment', apiError(e));
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd+Enter on macOS, Ctrl+Enter elsewhere.
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void submit();
    }
  };

  const handleDelete = async (c: Comment) => {
    try {
      await deleteComment.mutateAsync({
        commentId: c.id,
        targetType: target.type,
        targetId: target.id,
      });
    } catch (e) {
      toast.error('Delete failed', apiError(e));
    }
  };

  const canDelete = (c: Comment): boolean => {
    if (isStaffPlus) return true;
    return !!user && c.author_id === user.id;
  };

  return (
    <Card title="Comments">
      {active.isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : active.isError ? (
        <div className="text-sm text-red-600">
          {apiError(active.error) || 'Failed to load comments.'}
        </div>
      ) : comments.length === 0 ? (
        <p className="text-xs text-slate-400 py-1">No comments yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {comments.map((c) => (
            <li key={c.id} className="py-3 flex items-start gap-3">
              <div
                className="w-8 h-8 flex items-center justify-center bg-bb-navy/10 text-bb-navy text-xs font-semibold shrink-0"
                aria-hidden="true"
              >
                {initialsFor(c.author_name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="font-medium text-slate-800">
                    {c.author_name ?? 'Unknown user'}
                  </span>
                  <span>
                    {formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}
                  </span>
                </div>
                <div className="text-sm text-slate-800 mt-1 whitespace-pre-wrap break-words">
                  {c.body}
                </div>
              </div>
              {canDelete(c) && (
                <button
                  type="button"
                  onClick={() => handleDelete(c)}
                  disabled={deleteComment.isPending}
                  className="text-slate-400 hover:text-red-600 p-1 disabled:opacity-40"
                  aria-label="Delete comment"
                  title="Delete comment"
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 border-t border-slate-100 pt-3">
        <p className="text-xs text-slate-500 mb-2">
          Comments are visible to everyone with access to this file or request. Use Cmd+Enter to send.
        </p>
        <label htmlFor="comments-new-body" className="sr-only">
          Write a comment
        </label>
        <textarea
          id="comments-new-body"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Write a comment…"
          disabled={isPosting}
          className="w-full border border-bb-lilac bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-bb-navy/30 focus:border-bb-navy disabled:opacity-60"
        />
        <div className="flex items-center justify-between mt-2">
          <span className="text-[11px] text-slate-400">
            Press Cmd/Ctrl + Enter to send
          </span>
          <Button
            variant="primary"
            onClick={submit}
            loading={isPosting}
            disabled={!body.trim() || isPosting}
            className="w-auto"
          >
            Send
          </Button>
        </div>
      </div>
    </Card>
  );
}
